import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import io from "socket.io-client";
import axiosInstance from "../services/axiosInterceptor";
import { getToken } from "../services/authorize";
import { useLoading } from "../contexts/LoadingContext";

const API = process.env.REACT_APP_API;

const EMPTY_DATA = { schoolDays: 0, totals: { scheduled: 0, checked: 0, missed: 0 }, periodSummary: [], teachers: [], missed: [] };

const THAI_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const THAI_MONTHS_FULL = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];

const fmtYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtThaiShort = (d) => `${d.getDate()} ${THAI_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;

const getWeekStart = (d) => {
  const w = new Date(d);
  w.setHours(0, 0, 0, 0);
  w.setDate(w.getDate() - w.getDay()); // เริ่มสัปดาห์วันอาทิตย์ (ตรงกับ teach_day 0)
  return w;
};

// การ์ด donut สถานะการเช็คชื่อของ 1 คาบ
const makePeriodDonutOptions = (checked, scheduled, label) => ({
  labels: ["เข้าสอน", "ขาดสอน"],
  colors: ["#198754", "#dc3545"],
  chart: { type: "donut", toolbar: { show: false }, animations: { enabled: false } },
  legend: { show: false },
  dataLabels: { enabled: false },
  plotOptions: { pie: { donut: { size: "68%", labels: {
    show: true,
    name: { show: true, fontSize: "10px", offsetY: -4 },
    value: { show: true, fontSize: "13px", fontWeight: 700, offsetY: 4, formatter: () => `${checked}/${scheduled}` },
    total: { show: true, label, fontSize: "10px", color: "#6c757d", formatter: () => `${checked}/${scheduled}` },
  } } } },
  tooltip: { y: { formatter: (v) => `${v} คาบ` } },
  stroke: { width: 1 },
});

export default function HrTeachingAttendance() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();

  const [lastestYear, setLastestYear] = useState(null);
  const [mode, setMode] = useState("day"); // day | week | month
  const [selectedDate, setSelectedDate] = useState(() => fmtYMD(new Date()));
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const [data, setData] = useState(EMPTY_DATA);
  const [search, setSearch] = useState("");
  const [onlyMissed, setOnlyMissed] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  // มุมมองรายชื่อครู: ตาราง (จอใหญ่) | การ์ด (มือถือ) — เริ่มเป็นการ์ดถ้าจอเล็ก
  const [viewMode, setViewMode] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? "card" : "table"
  );

  // ─── ช่วงวันที่ตามโหมด ─────────────────────────────────────────────────────
  const { startDate, endDate, rangeLabel } = useMemo(() => {
    if (mode === "day") {
      const d = new Date(selectedDate);
      return { startDate: selectedDate, endDate: selectedDate, rangeLabel: fmtThaiShort(d) };
    }
    if (mode === "week") {
      const end = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
      return { startDate: fmtYMD(weekStart), endDate: fmtYMD(end), rangeLabel: `${fmtThaiShort(weekStart)} – ${fmtThaiShort(end)}` };
    }
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    return { startDate: fmtYMD(first), endDate: fmtYMD(last), rangeLabel: `${THAI_MONTHS_FULL[m - 1]} ${y + 543}` };
  }, [mode, selectedDate, weekStart, month]);

  useEffect(() => {
    axiosInstance.get(`${API}/getLastestStudyYear`)
      .then((res) => { if (res.data?.year_id) setLastestYear(res.data.year_id); })
      .catch(() => {});
  }, []);

  // Socket refresh (below) is registered once per lastestYear, so its callback
  // can't read the current range from the closure — it reads this ref instead.
  const rangeRef = useRef({ startDate, endDate });
  useEffect(() => { rangeRef.current = { startDate, endDate }; }, [startDate, endDate]);

  // silent = live socket refresh: keep the current range, don't show the
  // full-screen spinner or collapse expanded teacher rows.
  const fetchData = (silent = false) => {
    if (!lastestYear) return;
    const { startDate: s, endDate: e } = rangeRef.current;
    if (!silent) { setLoading(true); setExpanded(new Set()); }
    axiosInstance.get(`${API}/getTeachingMissByRange/${s}/${e}/${lastestYear}`)
      .then((res) => setData(res.data || EMPTY_DATA))
      .catch(() => setData(EMPTY_DATA))
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => {
    if (!lastestYear) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastestYear, startDate, endDate]);

  // Live update: teachers checking/updating attendance emit refreshGeneralAttendance.
  useEffect(() => {
    if (!lastestYear) return;
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });
    socket.on("refreshGeneralAttendance", () => fetchData(true));
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastestYear]);

  const shiftWeek = (n) => setWeekStart((w) => new Date(w.getFullYear(), w.getMonth(), w.getDate() + n * 7));

  const { scheduled, checked, missed } = data.totals;
  const pct = scheduled > 0 ? Math.round((checked / scheduled) * 100) : 0;

  // รายการคาบที่ขาดสอน จัดกลุ่มตามครู (สำหรับ expand)
  const missedByTeacher = useMemo(() => {
    const map = new Map();
    data.missed.forEach((r) => {
      const k = r.t_id ?? 0;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });
    return map;
  }, [data.missed]);

  const teacherRows = useMemo(() => {
    const q = search.trim();
    return data.teachers.filter((t) =>
      (!onlyMissed || t.missed > 0) &&
      (!q || (t.t_name || "").includes(q))
    );
  }, [data.teachers, search, onlyMissed]);

  const toggleExpand = (tid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(tid) ? next.delete(tid) : next.add(tid);
      return next;
    });
  };

  const kpis = [
    { label: "คาบที่ต้องสอน", value: scheduled, color: "primary", icon: "ri-calendar-check-line" },
    { label: "เข้าสอน/เช็คชื่อแล้ว", value: checked, color: "success", icon: "ri-checkbox-circle-line" },
    { label: "ขาดสอน/ไม่เช็คชื่อ", value: missed, color: "danger", icon: "ri-close-circle-line" },
    { label: "อัตราเข้าสอน", value: `${pct}%`, color: "info", icon: "ri-percent-line" },
  ];

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">งานบุคลากร</Link></li>
              <li className="breadcrumb-item active">การเข้าสอนของครู</li>
            </ol>
            <h4 className="main-title mb-0">การเข้าสอนของครู</h4>
            <p className="text-muted fs-sm mb-0">
              {rangeLabel}
              {data.schoolDays > 0 && <span className="ms-2">· {data.schoolDays} วันเปิดเรียน</span>}
            </p>
          </div>

          {/* Mode + date controls */}
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="btn-group btn-group-sm">
              {[["day", "รายวัน"], ["week", "รายสัปดาห์"], ["month", "รายเดือน"]].map(([v, label]) => (
                <button key={v} className={`btn btn${mode === v ? "" : "-outline"}-primary`} onClick={() => setMode(v)}>{label}</button>
              ))}
            </div>

            {mode === "day" && (
              <Form.Control
                type="date" size="sm" style={{ width: "auto" }}
                value={selectedDate} max={fmtYMD(new Date())}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            )}
            {mode === "week" && (
              <div className="d-flex align-items-center gap-1">
                <Button variant="outline-secondary" size="sm" onClick={() => shiftWeek(-1)} title="สัปดาห์ก่อน"><i className="ri-arrow-left-s-line" /></Button>
                <Button variant="outline-primary" size="sm" onClick={() => setWeekStart(getWeekStart(new Date()))}>สัปดาห์นี้</Button>
                <Button variant="outline-secondary" size="sm" onClick={() => shiftWeek(1)} title="สัปดาห์ถัดไป"><i className="ri-arrow-right-s-line" /></Button>
              </div>
            )}
            {mode === "month" && (
              <Form.Control
                type="month" size="sm" style={{ width: "auto" }}
                value={month} max={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`}
                onChange={(e) => setMonth(e.target.value)}
              />
            )}
          </div>
        </div>

        {/* KPI cards */}
        <Row className="g-3 mb-4">
          {kpis.map((k) => (
            <Col key={k.label} xs={6} xl={3}>
              <Card className={`card-one border-${k.color} border-start border-4`}>
                <Card.Body className="p-3">
                  <div className={`text-${k.color} mb-1`}><i className={`${k.icon} fs-40 lh-1`}></i></div>
                  <h6 className="fw-semibold text-dark mb-1">{k.label}</h6>
                  <h3 className={`fw-bold text-${k.color} mb-0`}>{k.value}</h3>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-3 mb-4">
          {/* Per-period status */}
          <Col xl={5}>
            <Card className="card-one h-100">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">สถานะการเช็คชื่อรายคาบ</Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                {data.periodSummary.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="ri-time-line fs-3 d-block mb-1"></i>
                    ไม่มีข้อมูลการสอนในช่วงที่เลือก
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-3 justify-content-center">
                      {data.periodSummary.map((p) => {
                        const pctP = p.scheduled > 0 ? Math.round((p.checked / p.scheduled) * 100) : 0;
                        return (
                          <div key={p.period_order} className="text-center" style={{ width: 115 }}>
                            <div className="fs-xs fw-semibold text-secondary mb-0">คาบ {p.period_order}</div>
                            <div className="fs-xs text-muted mb-1" style={{ fontSize: "10px" }}>{p.period_begin}–{p.period_end}</div>
                            <ReactApexChart
                              series={[p.checked, p.missed]}
                              options={makePeriodDonutOptions(p.checked, p.scheduled, `คาบ ${p.period_order}`)}
                              type="donut" height={110} width="100%"
                            />
                            <div className="fs-xs mt-1">
                              <span className="text-success fw-semibold">{p.checked}</span>
                              <span className="text-muted"> / {p.scheduled} ({pctP}%)</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="d-flex flex-wrap justify-content-center gap-3 fs-xs text-muted mt-2 pt-2 border-top">
                      <span><i className="ri-checkbox-blank-circle-fill me-1" style={{ color: "#198754" }} />เข้าสอน</span>
                      <span><i className="ri-checkbox-blank-circle-fill me-1" style={{ color: "#dc3545" }} />ขาดสอน</span>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Teacher list — who missed */}
          <Col xl={7}>
            <Card className="card-one h-100">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">ครูที่ขาดสอน / ยังไม่เช็คชื่อ</Card.Title>
                <Badge bg="danger" className="ms-1">{data.teachers.filter((t) => t.missed > 0).length} คน</Badge>
                <div className="d-flex align-items-center gap-2 ms-auto">
                  <Form.Check
                    type="switch" id="onlyMissed" label="เฉพาะที่ขาดสอน"
                    checked={onlyMissed} onChange={(e) => setOnlyMissed(e.target.checked)}
                    className="fs-sm"
                  />
                  <Form.Control
                    type="search" size="sm" placeholder="ค้นหาชื่อครู" style={{ maxWidth: 160 }}
                    value={search} onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="btn-group btn-group-sm">
                    <button
                      className={`btn btn${viewMode === "table" ? "" : "-outline"}-secondary`}
                      onClick={() => setViewMode("table")} title="มุมมองตาราง"
                    ><i className="ri-table-line" /></button>
                    <button
                      className={`btn btn${viewMode === "card" ? "" : "-outline"}-secondary`}
                      onClick={() => setViewMode("card")} title="มุมมองการ์ด"
                    ><i className="ri-layout-grid-line" /></button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {teacherRows.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="ri-checkbox-circle-line fs-3 text-success d-block mb-1"></i>
                    {data.teachers.length === 0 ? "ไม่มีข้อมูลการสอนในช่วงที่เลือก" : "ไม่พบครูที่ตรงเงื่อนไข"}
                  </div>
                ) : viewMode === "card" ? (
                  <div className="p-3 d-flex flex-column gap-2" style={{ maxHeight: 460, overflowY: "auto" }}>
                    {teacherRows.map((t) => {
                      const tid = t.t_id ?? 0;
                      const isOpen = expanded.has(tid);
                      const rows = missedByTeacher.get(tid) || [];
                      return (
                        <div key={tid} className={`border rounded p-2 ${t.missed > 0 ? "" : "bg-light"}`}>
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <span className="fw-semibold">{t.t_name}</span>
                            {t.missed > 0
                              ? <Badge bg="danger">ขาดสอน {t.missed}</Badge>
                              : <Badge bg="success">ครบ</Badge>}
                          </div>
                          <div className="d-flex flex-wrap gap-3 mt-1 fs-xs">
                            <span className="text-muted">ต้องสอน <b className="text-dark">{t.scheduled}</b></span>
                            <span className="text-muted">เข้าสอน <b className="text-success">{t.checked}</b></span>
                            <span className="text-muted">ขาดสอน <b className={t.missed > 0 ? "text-danger" : "text-success"}>{t.missed}</b></span>
                          </div>
                          {rows.length > 0 && (
                            <button className="btn btn-sm btn-link p-0 mt-1" onClick={() => toggleExpand(tid)}>
                              <i className={`ri-arrow-${isOpen ? "up" : "down"}-s-line`} /> {isOpen ? "ซ่อนรายละเอียด" : "ดูรายละเอียด"}
                            </button>
                          )}
                          {isOpen && rows.length > 0 && (
                            <div className="mt-2 d-flex flex-column gap-1">
                              {rows.map((r, i) => (
                                <div key={i} className="bg-white border rounded px-2 py-1 fs-xs">
                                  <div className="d-flex justify-content-between">
                                    <span className="fw-semibold">{fmtThaiShort(new Date(r.att_date))} · คาบ {r.period_order}</span>
                                    <span className="text-muted">ม.{r.c_id}/{r.r_no}</span>
                                  </div>
                                  <div className="text-muted">{r.subject || "-"}</div>
                                  <div>
                                    {r.reason
                                      ? <span className="text-warning">{r.reason}</span>
                                      : <span className="text-danger">ขาดสอน</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ maxHeight: 460, overflowY: "auto" }}>
                    <Table responsive hover size="sm" className="mb-0 align-middle">
                      <thead className="table-secondary sticky-top">
                        <tr>
                          <th style={{ minWidth: 160 }}>ครู</th>
                          <th className="text-center" style={{ width: 80 }}>ต้องสอน</th>
                          <th className="text-center" style={{ width: 80 }}>เข้าสอน</th>
                          <th className="text-center" style={{ width: 80 }}>ขาดสอน</th>
                          <th className="text-center" style={{ width: 60 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherRows.map((t) => {
                          const tid = t.t_id ?? 0;
                          const isOpen = expanded.has(tid);
                          const rows = missedByTeacher.get(tid) || [];
                          return (
                            <React.Fragment key={tid}>
                              <tr className={t.missed > 0 ? "" : "text-muted"}>
                                <td className="fw-semibold">{t.t_name}</td>
                                <td className="text-center">{t.scheduled}</td>
                                <td className="text-center text-success">{t.checked}</td>
                                <td className="text-center">
                                  {t.missed > 0
                                    ? <Badge bg="danger">{t.missed}</Badge>
                                    : <span className="text-success">0</span>}
                                </td>
                                <td className="text-center">
                                  {rows.length > 0 && (
                                    <button className="btn btn-sm btn-link p-0" onClick={() => toggleExpand(tid)} title="ดูรายละเอียด">
                                      <i className={`ri-arrow-${isOpen ? "up" : "down"}-s-line`} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {isOpen && rows.length > 0 && (
                                <tr>
                                  <td colSpan={5} className="p-0">
                                    <div className="bg-light px-3 py-2">
                                      <Table size="sm" className="mb-0 fs-xs">
                                        <thead>
                                          <tr className="text-muted">
                                            <th style={{ width: 110 }}>วันที่</th>
                                            <th style={{ width: 60 }}>คาบ</th>
                                            <th style={{ width: 90 }}>ห้อง</th>
                                            <th>วิชา</th>
                                            <th style={{ width: 140 }}>หมายเหตุ</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {rows.map((r, i) => (
                                            <tr key={i}>
                                              <td>{fmtThaiShort(new Date(r.att_date))}</td>
                                              <td>คาบ {r.period_order}</td>
                                              <td>ม.{r.c_id}/{r.r_no}</td>
                                              <td>{r.subject || <span className="text-muted">-</span>}</td>
                                              <td>
                                                {r.reason
                                                  ? <span className="text-warning">{r.reason}</span>
                                                  : <span className="text-danger">ขาดสอน</span>}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <p className="text-muted fs-xs">
          * "ขาดสอน" = มีคาบตามตารางสอนแต่ไม่มีการเช็คชื่อในคาบนั้น นับเฉพาะวันที่โรงเรียนเปิดเรียน (มีการเช็คชื่ออย่างน้อย 1 ห้อง)
          และคาบที่เลยเวลาแล้ว หมายเหตุจะกำกับหากครูลา / ไปอบรม / ออกนอกสถานที่ที่อนุมัติแล้ว
        </p>

        <Footer />
      </div>
    </React.Fragment>
  );
}
